"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractParkedSection = extractParkedSection;
exports.buildForwardSignal = buildForwardSignal;
const derived_1 = require("./derived");
const planUtils_1 = require("./planUtils");
const dates_1 = require("./dates");
/**
 * Lift a parked-decision section from a doc body: the first `## Open questions` / `deferred`
 * / `parked` heading (levels 2–4) and the lines under it up to the next same-or-higher
 * heading. Pure string work — a verbatim extract, no judgement (keeps it Tier-1). Returns
 * null when no such section exists or it is empty.
 */
function extractParkedSection(body) {
    const lines = body.split('\n');
    const headRe = /^(#{2,4})\s+.*\b(open questions|deferred|parked)\b/i;
    let start = -1;
    let level = 0;
    for (let i = 0; i < lines.length; i++) {
        const m = lines[i].match(headRe);
        if (m) {
            start = i;
            level = m[1].length;
            break;
        }
    }
    if (start === -1)
        return null;
    const body_ = [];
    for (let i = start + 1; i < lines.length; i++) {
        const h = lines[i].match(/^(#{1,6})\s+/);
        if (h && h[1].length <= level)
            break;
        body_.push(lines[i]);
    }
    const text = body_.join('\n').trim();
    return text.length > 0 ? text : null;
}
/**
 * Inclusive `created` date window (YYYY-MM-DD prefix compare), matching selectReportDocs.
 * `created` may load as a Date (gray-matter) — canonicalize first so the compare is safe;
 * an unknown/empty date is not filtered out (the lenient selectReportDocs behaviour).
 */
function inDateWindow(created, from, to) {
    const c = ((0, dates_1.toCanonical)(created) ?? '').slice(0, 10);
    if (!c)
        return true;
    if (from && c < from)
        return false;
    if (to && c > to)
        return false;
    return true;
}
/** Whole-days between a doc's created date and `now` (floored, never negative). */
function ageInDays(created, nowEpoch) {
    if (!created)
        return 0;
    const e = (0, dates_1.toEpoch)(created);
    if (!Number.isFinite(e))
        return 0;
    return Math.max(0, Math.floor((nowEpoch - e) / 86400000));
}
/** A thread is finished work — never a source of next-work signal. */
function isThreadDone(thread) {
    const deliverables = (thread.allDocs ?? []).filter(d => d.type !== 'ctx' && d.type !== 'reference' && d.type !== 'req' && d.type !== 'thread');
    return deliverables.length > 0 && deliverables.every(d => d.status === 'done');
}
function buildForwardSignal(state, filters = {}, now = (0, dates_1.today)()) {
    const weaveFilter = filters.weaves && filters.weaves.length ? new Set(filters.weaves) : null;
    const threadFilter = filters.threads && filters.threads.length ? new Set(filters.threads) : null;
    const from = filters.from ?? null;
    const to = filters.to ?? null;
    const nowEpoch = (0, dates_1.toEpoch)(now);
    // Dependents map (roadmap fan-out): th_ ULID → how many threads depend on it. The
    // leverage proxy for stalled/blocked threads — resolving one releases its dependents.
    const roadmap = (0, derived_1.buildRoadmap)(state);
    const dependents = new Map();
    for (const node of roadmap.roadmap) {
        for (const dep of node.dependsOn)
            dependents.set(dep, (dependents.get(dep) ?? 0) + 1);
    }
    const nodeByThread = new Map();
    for (const node of roadmap.roadmap)
        nodeByThread.set(`${node.weaveSlug}/${node.threadSlug}`, node);
    const items = [];
    const passesScope = (weaveSlug, threadSlug, created) => (!weaveFilter || weaveFilter.has(weaveSlug)) &&
        (!threadFilter || threadFilter.has(threadSlug)) &&
        inDateWindow(created, from, to);
    for (const weave of state.weaves ?? []) {
        for (const thread of weave.threads ?? []) {
            if (isThreadDone(thread))
                continue;
            const { idea, design, req, plans } = thread;
            const node = nodeByThread.get(`${weave.id}/${thread.id}`);
            const threadDependents = node?.ulid ? (dependents.get(node.ulid) ?? 0) : 0;
            const downstreamDocs = (design ? 1 : 0) + (req ? 1 : 0) + (plans?.length ?? 0);
            // --- parked-decision: an Open-questions section on the idea/design -----------
            for (const doc of [idea, design]) {
                if (!doc)
                    continue;
                if (!passesScope(weave.id, thread.id, doc.created ?? ''))
                    continue;
                const parked = extractParkedSection(doc.content ?? '');
                if (!parked)
                    continue;
                const firstLine = parked.split('\n').find(l => l.trim().length > 0)?.trim() ?? '';
                items.push({
                    group: 'parked-decision',
                    docId: doc.id, docType: doc.type, weaveSlug: weave.id, threadSlug: thread.id,
                    title: doc.title,
                    detail: `Parked "Open questions" on the ${doc.type} awaiting a call — ${firstLine.slice(0, 160)}`,
                    leverage: downstreamDocs,
                    ready: true,
                    ageDays: ageInDays(doc.created ?? '', nowEpoch),
                });
            }
            // --- stalled-intent: a broken authoring chain (idea→design→plan) -------------
            const stall = (() => {
                if (idea && !design)
                    return { doc: idea, what: 'idea has no design — intent never advanced to how' };
                if (design && (plans?.length ?? 0) === 0)
                    return { doc: design, what: 'design has no plan — how was never broken into steps' };
                const notStarted = (plans ?? []).find(p => p.status === 'active' || p.status === 'draft');
                const anyStarted = (plans ?? []).some(p => p.status === 'implementing' || p.status === 'done');
                if (notStarted && !anyStarted)
                    return { doc: notStarted, what: `plan is ${notStarted.status} but was never started` };
                return null;
            })();
            if (stall && passesScope(weave.id, thread.id, stall.doc.created ?? '')) {
                items.push({
                    group: 'stalled-intent',
                    docId: stall.doc.id, docType: stall.doc.type, weaveSlug: weave.id, threadSlug: thread.id,
                    title: stall.doc.title,
                    detail: `Stalled: ${stall.what}`,
                    leverage: threadDependents,
                    ready: true,
                    ageDays: ageInDays(stall.doc.created ?? '', nowEpoch),
                });
            }
            // --- blocked-work: blocked steps in this thread's implementing plans ---------
            for (const plan of plans ?? []) {
                if (plan.status !== 'implementing')
                    continue;
                if (!passesScope(weave.id, thread.id, plan.created ?? ''))
                    continue;
                for (const step of plan.steps ?? []) {
                    if (step.status === 'done' || step.status === 'cancelled')
                        continue;
                    if (!(0, planUtils_1.isStepBlocked)(step, plan, state.index))
                        continue;
                    const dependentSteps = (plan.steps ?? []).filter(s => (s.blockedBy ?? []).includes(step.id)).length;
                    items.push({
                        group: 'blocked-work',
                        docId: plan.id, docType: plan.type, weaveSlug: weave.id, threadSlug: thread.id,
                        title: plan.title,
                        detail: `Blocked step "${step.description.slice(0, 120)}" (waiting on: ${(step.blockedBy ?? []).join(', ')})`,
                        leverage: dependentSteps,
                        ready: false,
                        ageDays: ageInDays(plan.created ?? '', nowEpoch),
                        refs: step.blockedBy ?? [],
                    });
                }
            }
        }
        // --- drift-debt: actionable stale docs in this weave -----------------------------
        for (const entry of (0, derived_1.staleEntries)(weave)) {
            if (!entry.actionable)
                continue;
            const thread = (weave.threads ?? []).find(t => t.id === entry.threadSlug);
            const doc = (thread?.allDocs ?? []).find(d => d.id === entry.docId);
            const created = doc?.created ?? '';
            if (!passesScope(entry.weaveSlug, entry.threadSlug, created))
                continue;
            items.push({
                group: 'drift-debt',
                docId: entry.docId, docType: entry.type, weaveSlug: entry.weaveSlug, threadSlug: entry.threadSlug,
                title: doc?.title ?? entry.docId,
                detail: `Stale (${entry.reason}): ${entry.detail} — never reconciled`,
                leverage: 1,
                ready: true,
                ageDays: ageInDays(created, nowEpoch),
            });
        }
    }
    // --- blocked-work: dependency-blocked threads (roadmap blockedOn) --------------------
    for (const node of roadmap.roadmap) {
        if (node.status !== 'blocked' || node.blockedOn.length === 0)
            continue;
        if (!passesScope(node.weaveSlug, node.threadSlug, node.created ?? ''))
            continue;
        items.push({
            group: 'blocked-work',
            docId: node.ulid ?? `${node.weaveSlug}/${node.threadSlug}`,
            docType: 'thread', weaveSlug: node.weaveSlug, threadSlug: node.threadSlug,
            title: node.title,
            detail: `Thread dependency-blocked on ${node.blockedOn.length} unfinished dep(s): ${node.blockedOn.join(', ')}`,
            leverage: node.ulid ? (dependents.get(node.ulid) ?? 0) : 0,
            ready: false,
            ageDays: ageInDays(node.created ?? '', nowEpoch),
            refs: node.blockedOn,
        });
    }
    // Deterministic ranking: leverage desc, ready-first, oldest first, then id (stable).
    items.sort((a, b) => b.leverage - a.leverage ||
        (a.ready === b.ready ? 0 : a.ready ? -1 : 1) ||
        b.ageDays - a.ageDays ||
        (a.docId < b.docId ? -1 : a.docId > b.docId ? 1 : 0));
    const counts = {
        'parked-decision': 0, 'stalled-intent': 0, 'blocked-work': 0, 'drift-debt': 0,
    };
    for (const it of items)
        counts[it.group] += 1;
    return { items, counts, totalItems: items.length, filters, isEmpty: items.length === 0 };
}
